import React, { useState } from 'react';
import { ListChecks, Plus, Edit2, Trash2, Check, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { activityService } from '@/lib/api/activity.service';
import type { ActivityWithAssociations, ActivityItem } from '@/types/activity.types';

interface ActivityItemsListProps {
  activity: ActivityWithAssociations;
  onUpdate: () => void;
}

export const ActivityItemsList: React.FC<ActivityItemsListProps> = ({ activity, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddInput, setShowAddInput] = useState(false);
  const [newItemContent, setNewItemContent] = useState('');
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');

  const handleAddItem = async () => {
    if (!newItemContent.trim()) {
      setError('Item content cannot be empty');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await activityService.addItem(activity.id, newItemContent.trim());
      setNewItemContent('');
      setShowAddInput(false);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add item');
      console.error('Error adding item:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (item: ActivityItem) => {
    setEditingItemId(item.id);
    setEditContent(item.content);
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditContent('');
    setError(null);
  };

  const handleSaveEdit = async (itemId: number) => {
    if (!editContent.trim()) {
      setError('Item content cannot be empty');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await activityService.updateItem(itemId, editContent.trim());
      setEditingItemId(null);
      setEditContent('');
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update item');
      console.error('Error updating item:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!confirm('Are you sure you want to delete this item?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await activityService.deleteItem(itemId);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete item');
      console.error('Error deleting item:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      action();
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ListChecks className="h-5 w-5" />
              Activity Items
            </CardTitle>
            <CardDescription>
              Notes, checklists, and reminders for this activity
            </CardDescription>
          </div>
          {!showAddInput && (
            <Button
              size="sm"
              onClick={() => setShowAddInput(true)}
              disabled={loading}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}

        {/* Add New Item Input */}
        {showAddInput && (
          <div className="mb-4 p-3 bg-purple-50 border-2 border-purple-200 rounded-lg">
            <Input
              placeholder="Enter item content..."
              value={newItemContent}
              onChange={(e) => setNewItemContent(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, handleAddItem)}
              autoFocus
              disabled={loading}
              className="mb-2"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowAddInput(false);
                  setNewItemContent('');
                  setError(null);
                }}
                disabled={loading}
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleAddItem}
                disabled={loading || !newItemContent.trim()}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Check className="h-4 w-4 mr-1" />
                )}
                Add
              </Button>
            </div>
          </div>
        )}

        {/* Items List */}
        {activity.items && activity.items.length > 0 ? (
          <div className="space-y-2">
            {activity.items.map((item, index) => (
              <div
                key={item.id}
                className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-medium mt-0.5">
                  {index + 1}
                </div>

                {editingItemId === item.id ? (
                  <div className="flex-1">
                    <Input
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      onKeyPress={(e) => handleKeyPress(e, () => handleSaveEdit(item.id))}
                      disabled={loading}
                      className="mb-2"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelEdit}
                        disabled={loading}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSaveEdit(item.id)}
                        disabled={loading || !editContent.trim()}
                      >
                        {loading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <Check className="h-4 w-4 mr-1" />
                        )}
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex-1">
                      <p className="text-gray-900 whitespace-pre-wrap">{item.content}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Added {new Date(item.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStartEdit(item)}
                        disabled={loading}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteItem(item.id)}
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">
            No items yet. Click "Add Item" to create notes, checklists, or reminders.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
